import {useCallback, useRef, useState} from 'react';
import {CommonService} from '../services';
import moment, {Moment} from "moment";
import {AXIOS_REQUEST_CANCELLED} from "../services/api.service";
import {debounce} from 'lodash';

type HttpMethod = 'get' | 'post' | 'put' | 'delete';

interface IFetchData {
    url: string;
    method?: HttpMethod;
    payload?: any;
    headers?: HeadersInit;
}

interface FetchResult<T> {
    response: T | null;
    error: any | null;
    loading: boolean;
    loaded: boolean;
    loadingFailed: boolean;
    requestedAt?: Moment;
    fetchData: (config: IFetchData) => void;
}

const useFetch = <T>(): FetchResult<T> => {

    const [response, setResponse] = useState<T | null>(null);
    const [requestedAt, setRequestedAt] = useState<Moment>();
    const [error, setError] = useState<Error | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [loaded, setLoaded] = useState<boolean>(false);
    const [loadingFailed, setLoadingFailed] = useState<boolean>(false);
    const APICallSubscription = useRef<any>(null);

    const fetchData = useCallback((config: IFetchData) => {
        const {url, method = 'get', payload, headers} = config;
        if (!url) return;
        const cancelTokenSource = CommonService.getCancelToken();
        if (APICallSubscription && APICallSubscription.current) {
            APICallSubscription.current.cancel();
        }
        APICallSubscription.current = cancelTokenSource;
        setLoading(true);
        setLoaded(false);
        setLoadingFailed(false);
        let apiCall: any;
        switch (method) {
            case 'get':
                apiCall = CommonService._api.get;
                break;
            case 'post':
                apiCall = CommonService._api.post;
                break;
            case 'put':
                apiCall = CommonService._api.put;
                break;
            case 'delete':
                apiCall = CommonService._api.delete;
                break;
            default:
                apiCall = CommonService._api.get;
                break;
        }
        setRequestedAt(moment());
        debounce(() => {
            apiCall(url, payload, headers, {cancelToken: cancelTokenSource.token})
                .then((res: any) => {
                    setResponse(res?.data || res);
                    setLoaded(true);
                    setLoading(false);
                    setLoadingFailed(false);
                })
                .catch((error: any) => {
                    console.log(error);
                    if (error.reason !== AXIOS_REQUEST_CANCELLED) { // if previous request got cancelled do not close loading state
                        setError(error);
                        setResponse(null);
                        setLoaded(false);
                        setLoadingFailed(true);
                        setLoading(false);
                    }
                    if (error.message || error.error) {
                        CommonService._alert.showToast(error.message || error.error, "error")
                    }
                });
        }, 1000)();
    }, []);

    return {response, error, loading, loaded, loadingFailed, requestedAt, fetchData};
};

export default useFetch;
